/**
 * What a write to the catalogue amounted to.
 *
 * A write is two things in one call, in this order: the resource is **declared**
 * — written to the desired state this control plane keeps — and then the
 * cluster is brought to it. The order is deliberate on the server's side: the
 * declaration is ours, and a cluster that could not be reached must not undo
 * what was asked for.
 *
 * So `502 tenant_unreachable` here does not mean nothing happened. It means the
 * declaration stands, the cluster is behind it, and the next application of the
 * desired state will bring it across — carrying no person's name, because by
 * then nobody is standing in front of it. A console that drew that as a plain
 * failure would have somebody type the resource in a second time.
 *
 * `operator_not_nameable` is its own outcome for the opposite reason: nothing
 * happened at all, on either side, and it is not fixed by trying again.
 */

import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { ClusterApi } from '../../api/client';
import type { ResourceInput } from '../../api/types';

export type WriteOutcome =
  /** Declared, and the cluster has it. */
  | { done: 'delivered' }
  /** Declared. The cluster was not reached, and the declaration stands. */
  | { done: 'declared' }
  /** Nothing was written. */
  | { done: 'refused'; error: ApiError };

function outcomeOf(error: unknown): WriteOutcome {
  const failure = toApiError(error);
  return failure.code === 'tenant_unreachable'
    ? { done: 'declared' }
    : { done: 'refused', error: failure };
}

export async function writeResource(
  api: ClusterApi,
  id: string,
  input: ResourceInput,
): Promise<WriteOutcome> {
  try {
    await api.putResource(id, input);
    return { done: 'delivered' };
  } catch (error: unknown) {
    return outcomeOf(error);
  }
}

/**
 * Removing closes access: a request naming a resource the cluster's catalogue
 * does not have is refused before policy is consulted at all. Which is also why
 * a removal that only got as far as the declaration is worth saying out loud —
 * until it lands, the cluster still serves it.
 */
export async function removeResource(api: ClusterApi, id: string): Promise<WriteOutcome> {
  try {
    await api.deleteResource(id);
    return { done: 'delivered' };
  } catch (error: unknown) {
    return outcomeOf(error);
  }
}
